import { useCallback, useState } from 'react';
import { OperationVariables, QueryLazyOptions, useLazyQuery, useMutation } from '@apollo/client';
import { EXPORT_FORM_TO_PIMS } from 'pages/Conversations/graphql/mutations';
import { GET_FORM_PDF } from 'pages/Conversations/graphql/queries';
import { FormContent } from 'shared/types/forms';
import {
  FileExportData as FileExportData,
  FormExportErrorType as FormExportErrorType,
  ClinicPet as ClinicPet,
} from 'shared/types/graphql';
import { captureException } from '@sentry/react';
import { IntegrationSystemDisplayNameType, useIntegrationsProvider } from 'shared/providers/IntegrationsProvider';
import { useToast } from '@televet/kibble-ui/build/components/Toast';

// Helper Function
const formatNames = (petNames: string[]): string => {
  if (petNames.length === 0) {
    return '';
  }
  if (petNames.length === 1) {
    return petNames[0];
  }
  return petNames.slice(0, petNames.length - 1).join(', ') + ` and ${petNames.slice(-1)}`;
};

/// ERROR HANDLING
type ErrorContext = {
  petNames: string[];
  formName: string;
  integrationName: string;
};
const genericHelp = `Please try again or contact support for more help`;
const errorMessageHandlers = {
  [FormExportErrorType.GeneratingForm]: ({ petNames, integrationName, formName }: ErrorContext) => {
    return `Otto encountered an error while preparing ${formName} for export to ${integrationName} for ${formatNames(
      petNames,
    )}. Please contact support for more help`;
  },
  [FormExportErrorType.InvalidPet]: ({ petNames, formName, integrationName }: ErrorContext) => {
    return `Unable to export ${formName}, ${formatNames(petNames)} ${
      petNames.length > 1 ? 'are' : 'is'
    } not synced properly with ${integrationName}. ${genericHelp}`;
  },
  [FormExportErrorType.InvalidParent]: ({ petNames, formName, integrationName }: ErrorContext) => {
    return `Unable to export ${formName}, Could not find ${
      petNames.length > 1 ? 'owners' : 'an owner'
    } for ${formatNames(petNames)} that ${
      petNames.length > 1 ? 'are' : 'is'
    } synced with ${integrationName}. ${genericHelp}`;
  },
  [FormExportErrorType.PimsConfiguration]: ({ integrationName }: ErrorContext) => {
    return `${integrationName} is not configured correctly for form export, ${genericHelp}`;
  },
  [FormExportErrorType.Unauthorized]: ({ petNames, formName, integrationName }: ErrorContext) => {
    return `Otto could not authenticate with ${integrationName} while exporting ${formName} for ${formatNames(
      petNames,
    )}, ${genericHelp}`;
  },
  [FormExportErrorType.Throttled]: ({ formName, petNames, integrationName }: ErrorContext) => {
    return `Otto was throttled by ${integrationName} while exporting ${formName} for ${formatNames(
      petNames,
    )}. Please try again after five minutes or contact support for more help`;
  },
  [FormExportErrorType.PimsServer]: ({ integrationName }: ErrorContext) => {
    return `${integrationName} encountered an error during export, ${genericHelp}`;
  },
  [FormExportErrorType.Connection]: ({ formName, petNames, integrationName }: ErrorContext) => {
    return `Otto could not establish a connection with ${integrationName} while exporting ${formName} for ${formatNames(
      petNames,
    )}, ${genericHelp}`;
  },
  [FormExportErrorType.Unknown]: ({ formName, petNames, integrationName }: ErrorContext) => {
    return `Otto encountered an unknown error while exporting ${formName} to ${integrationName} for ${petNames}, ${genericHelp}`;
  },
} as Record<FormExportErrorType, (ctx: ErrorContext) => string>;

function createErrorMessages(
  results: PimsExportResponse[],
  pets: { id: string; name: string }[],
  formName: string,
  integrationName: IntegrationSystemDisplayNameType | null,
): string[] {
  const groupedErrors = new Map<FormExportErrorType, PimsExportResponse[]>();
  results.forEach((result) => {
    if (!result.success) {
      const errorType = (result.errorType as FormExportErrorType) || FormExportErrorType.Unknown;
      const stored = groupedErrors.get(errorType) || [];
      groupedErrors.set(errorType, [...stored, result]);
    }
  });
  const errorMessages: string[] = [];
  groupedErrors.forEach((value, key) => {
    const errorHandler = errorMessageHandlers[key] || errorMessageHandlers[FormExportErrorType.Unknown];
    const context = {
      petNames: value.map((v) => pets.find((p) => p.id === v.petId)?.name || '').filter((n) => !!n),
      formName,
      integrationName,
    } as ErrorContext;
    errorMessages.push(errorHandler(context));
  });
  return errorMessages;
}

// Custom helper hook
type DownloadFunction = (options?: QueryLazyOptions<OperationVariables> | undefined) => void;
// Todo: combine this
export const usePdfDownload = (
  formContent: FormContent,
  entityId?: string,
  pet?: ClinicPet | null,
): DownloadFunction => {
  const toast = useToast();
  const [getFormPdf] = useLazyQuery<{ formSubmissionExport: FileExportData }>(GET_FORM_PDF, {
    fetchPolicy: 'no-cache',
    variables: { formId: entityId },
  });

  return (): void => {
    getFormPdf()
      .then((formPdfExportData) => {
        if (formPdfExportData.data?.formSubmissionExport) {
          const linkSource = `data:application/pdf;base64,${formPdfExportData.data.formSubmissionExport.base64Encoding}`;
          const downloadLink = document.createElement('a');
          const fileName = `Otto_${formContent.formName}_${pet ? `${pet.name}_` : ''}${new Date().toISOString()}.pdf`;
          downloadLink.href = linkSource;
          downloadLink.download = fileName;
          downloadLink.click();
        } else {
          throw new Error('Error Generating Pdf');
        }
      })
      .catch(() => {
        toast({
          status: 'error',
          title: 'Error Generating PDF. Please try again or contact support.',
        });
      });
  };
};

type UseFormExportReturnType = {
  available: boolean;
  isSending: boolean;
  integrationName: IntegrationSystemDisplayNameType | null;
  exportFormToPims: (pets: { id: string; name: string }[]) => Promise<void>;
};

type PimsExportResponse = { success: boolean; petId: string; errorType?: string; errorMessage: string };
export const useFormExport = ({
  form: { id: formId, title: formName },
}: {
  form: {
    id: string;
    title: string;
  };
}): UseFormExportReturnType => {
  const toast = useToast();
  const [createExportToPims] = useMutation(EXPORT_FORM_TO_PIMS);
  const [isSending, setIsSending] = useState(false);
  const { primaryIntegrationName, isExportToPimsSupported } = useIntegrationsProvider();

  const available = !!formId && !!isExportToPimsSupported;

  const exportFormToPims = useCallback(
    async (pets: { id: string; name: string }[]) => {
      if (!available) {
        return;
      }
      try {
        setIsSending(true);
        const _result = await createExportToPims({
          variables: {
            formId,
            petIds: pets.map((p) => p.id),
          },
        });

        setIsSending(false);

        const results = (_result.data.formSubmissionExportToPims.results || []) as PimsExportResponse[];
        const completeSuccess = results.every((r) => r.success);
        if (completeSuccess) {
          toast({
            title: `Successfully exported ${formName} to ${primaryIntegrationName} for ${formatNames(
              pets.map((p) => p.name),
            )}`,
            status: 'success',
            id: 'export-pims-success',
          });
        } else {
          const errorMessages = createErrorMessages(results, pets, formName, primaryIntegrationName);
          errorMessages.forEach((message) => {
            toast({
              status: 'error',
              title: message,
            });
          });
        }
      } catch (err) {
        setIsSending(false);
        captureException(err);
        toast({
          status: 'error',
          title: 'Error Exporting to Database. Please try again or contact support.',
        });
      }
    },
    [available, createExportToPims, formId, formName, primaryIntegrationName, toast],
  );

  return {
    available,
    isSending,
    integrationName: primaryIntegrationName,
    exportFormToPims,
  };
};
